import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { alertTradePlaced, alertTradeClosed } from "./alerts";

const API_MAP = { mstock: "/api/mstock", zerodha: "/api/zerodha" };

/* ===== Export trades to CSV/Excel ===== */
function exportTradesToCSV(trades, summary, filename = "backtest_results") {
  if (!trades || trades.length === 0) return;

  // Summary rows
  const summaryRows = [
    ["=== BACKTEST SUMMARY ==="],
    ["Total Trades", summary.totalTrades],
    ["Wins", summary.wins],
    ["Losses", summary.losses],
    ["Win Rate", `${summary.winRate}%`],
    ["Total Index P&L", summary.totalPnl],
    ["Total Option P&L (net)", `₹${summary.totalOptionPnl}`],
    ["Total Charges", `₹${summary.totalCharges || 0}`],
    ["Capital/Trade", `₹${summary.capitalPerTrade || 0}`],
    ["Total Capital Used", `₹${summary.totalCapitalUsed || 0}`],
    ["ROI", `${summary.roi || 0}%`],
    ["Profit Factor", summary.profitFactor],
    ["Max Drawdown", summary.maxDrawdown],
    [""],
    ["=== TRADE LOG ==="],
  ];

  // Header row
  const headers = [
    "Type", "Signal Date", "Exit Date", "Entry", "SL", "Target", "Exit Price",
    "Exit Reason", "Result", "Index P&L (pts)", "R Multiple",
    "Option Type", "Strike", "Option Symbol",
    "Buy Premium", "Sell Premium", "Capital Used",
    "Gross P&L (₹)", "Charges (₹)", "Net P&L (₹)",
    "Candles Held", "Partial Exit", "Premium Source"
  ];

  // Trade rows
  const tradeRows = trades.map(t => [
    t.type,
    t.triggerDate,
    t.exitDate,
    t.entry?.toFixed(2),
    t.sl?.toFixed(2),
    t.target?.toFixed(2),
    t.exitPrice?.toFixed(2),
    t.exitReason,
    t.result,
    t.pnl?.toFixed(2),
    t.rMultiple?.toFixed(2),
    t.option?.type || "",
    t.option?.strike || "",
    t.option?.optionSymbol || "",
    t.option?.entryPremium?.toFixed(2) || "",
    t.option?.exitPremium?.toFixed(2) || "",
    t.option?.entryPremium && t.option?.qty ? Math.round(t.option.entryPremium * t.option.qty) : "",
    t.option?.moneyPnlGross || "",
    t.option?.charges?.totalCharges || "",
    t.option?.moneyPnl || "",
    t.candlesHeld || "",
    t.partialExit ? "Yes" : "No",
    t.option?.premiumSource || "ESTIMATED",
  ]);

  // Combine all rows
  const allRows = [...summaryRows, headers, ...tradeRows];

  // Convert to CSV
  const csvContent = allRows.map(row =>
    Array.isArray(row) ? row.map(cell => {
      const str = String(cell ?? "");
      return str.includes(",") || str.includes('"') || str.includes("\n")
        ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(",") : String(row)
  ).join("\n");

  // Add BOM for Excel to recognize UTF-8 (₹ symbol)
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* Open in Google Sheets: copy TSV to clipboard + open blank sheet */
const LIVE_HEADERS = ["Time","Type","Option","Qty","Entry","SL","Target","Entry Prem","Exit Prem","Capital","Gross P&L","Net P&L","Status","Exit Reason","Strategy","SIM/LIVE"];
function liveTradeRows(trades) { return trades.map(t => [new Date(t.time||t.placedAt).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit",second:"2-digit",hour12:false}), t.type, (t.signalType||"")+" "+(t.strike||""), t.qty, t.entry?.toFixed(2), t.sl?.toFixed(2), t.target?.toFixed(2), t.entryPremium?.toFixed(2)||"", t.exitPremium?.toFixed(2)||"", t.capital||"", t.grossPnl||"", t.netPnl||"", t.status, t.exitReason||"", t.strategy||"", t.simulated?"SIM":"LIVE"]); }
function openLiveTradesInSheets(trades) { if (!trades?.length) return; const rows=liveTradeRows(trades); const tsv=[LIVE_HEADERS,...rows].map(r=>r.join("\t")).join("\n"); const html="<table><tr>"+LIVE_HEADERS.map(h=>"<th>"+h+"</th>").join("")+"</tr>"+rows.map(r=>"<tr>"+r.map(c=>"<td>"+(c??"")+"</td>").join("")+"</tr>").join("")+"</table>"; navigator.clipboard.write([new ClipboardItem({"text/html":new Blob([html],{type:"text/html"}),"text/plain":new Blob([tsv],{type:"text/plain"})})]).then(()=>{window.open("https://docs.google.com/spreadsheets/create","_blank");const t=document.createElement("div");t.textContent="Data copied! Press Ctrl+V";t.style.cssText="position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#1a73e8;color:#fff;padding:12px 24px;border-radius:8px;font-size:14px;font-weight:600;z-index:9999;";document.body.appendChild(t);setTimeout(()=>t.remove(),5000)}).catch(()=>{navigator.clipboard.writeText(tsv).then(()=>{window.open("https://docs.google.com/spreadsheets/create","_blank");alert("Data copied! Paste with Ctrl+V")})}); }
function exportLiveTradesCSV(trades, filename) { if (!trades?.length) return; const csv=[LIVE_HEADERS,...liveTradeRows(trades)].map(r=>r.map(c=>{const s=String(c??"");return s.includes(",")?`"${s}"`:s;}).join(",")).join("\n"); const blob=new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8;"}); const a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download=`${filename}_${new Date().toISOString().slice(0,10)}.csv`;a.click(); }

function openInGoogleSheets(trades, summary) {
  if (!trades || trades.length === 0) return;

  const headers = [
    "Type", "Signal Date", "Exit Date", "Entry", "SL", "Target", "Exit Price",
    "Exit Reason", "Result", "Index P&L", "R",
    "Option", "Strike", "Symbol",
    "Buy Prem", "Sell Prem", "Capital",
    "Gross P&L", "Charges", "Net P&L", "Source"
  ];

  const rows = trades.map(t => [
    t.type, t.triggerDate, t.exitDate,
    t.entry?.toFixed(2), t.sl?.toFixed(2), t.target?.toFixed(2), t.exitPrice?.toFixed(2),
    t.exitReason, t.result, t.pnl?.toFixed(2), t.rMultiple?.toFixed(2),
    t.option?.type || "", t.option?.strike || "", t.option?.optionSymbol || "",
    t.option?.entryPremium?.toFixed(2) || "", t.option?.exitPremium?.toFixed(2) || "",
    t.option?.entryPremium && t.option?.qty ? Math.round(t.option.entryPremium * t.option.qty) : "",
    t.option?.moneyPnlGross || "", t.option?.charges?.totalCharges || "",
    t.option?.moneyPnl || "", t.option?.premiumSource || "EST",
  ]);

  // Summary block
  const summaryBlock = [
    ["BACKTEST SUMMARY"],
    ["Trades", summary.totalTrades, "Wins", summary.wins, "Losses", summary.losses],
    ["Win Rate", `${summary.winRate}%`, "P&L", `${summary.totalOptionPnl}`, "Charges", `${summary.totalCharges || 0}`],
    ["Capital/Trade", summary.capitalPerTrade || 0, "ROI", `${summary.roi || 0}%`, "PF", summary.profitFactor],
    [],
  ];

  const tsv = [...summaryBlock, headers, ...rows]
    .map(row => (Array.isArray(row) ? row.join("\t") : row))
    .join("\n");

  // Method 1: Try opening with data via HTML table paste
  // Method 2: Fallback to clipboard + new sheet
  const htmlTable = `<table><tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr>${rows.map(r => `<tr>${r.map(c => `<td>${c ?? ""}</td>`).join("")}</tr>`).join("")}</table>`;

  navigator.clipboard.write([
    new ClipboardItem({
      "text/html": new Blob([htmlTable], { type: "text/html" }),
      "text/plain": new Blob([tsv], { type: "text/plain" }),
    })
  ]).then(() => {
    window.open("https://docs.google.com/spreadsheets/create", "_blank");
    // Show a brief toast
    const toast = document.createElement("div");
    toast.textContent = "Data copied! Press Ctrl+V (Cmd+V) in Google Sheets to paste";
    toast.style.cssText = "position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#1a73e8;color:#fff;padding:12px 24px;border-radius:8px;font-size:14px;font-weight:600;z-index:9999;box-shadow:0 4px 20px rgba(0,0,0,0.3);";
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 5000);
  }).catch(() => {
    // Fallback
    navigator.clipboard.writeText(tsv).then(() => {
      window.open("https://docs.google.com/spreadsheets/create", "_blank");
      alert("Data copied to clipboard! Press Ctrl+V (Cmd+V) in Google Sheets to paste.");
    });
  });
}

/* ===== Candlestick Chart Modal ===== */
function CandleChart({ chartData, trade, onClose }) {
  const svgRef = useRef(null);
  const crossVRef = useRef(null);
  const crossHRef = useRef(null);
  const crossPriceRef = useRef(null);
  const crossPriceTextRef = useRef(null);
  const ohlcBarRef = useRef(null);
  const containerRef = useRef(null);
  const [chartSize, setChartSize] = useState({ w: 960, h: 480 });

  // Watch container resize
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 200 && height > 150) {
          setChartSize({ w: Math.round(width), h: Math.max(250, Math.round(height - 80)) }); // subtract header+ohlc bar
        }
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!chartData || !trade) return null;

  // Find exit candle index
  let exitIdx = -1;
  if (trade.exitDate) exitIdx = chartData.findIndex(c => c.date === trade.exitDate);
  if (exitIdx < 0 && trade.candlesHeld) exitIdx = trade.triggerIndex + trade.candlesHeld;

  const sigIdx = trade.triggerIndex - 1;
  const trgIdx = trade.triggerIndex;
  const lastRelevant = exitIdx > 0 ? Math.max(trgIdx, exitIdx) : trgIdx;

  // Limit to max 30 candles for readability, centered on the trade
  const tradeCenter = Math.floor((sigIdx + lastRelevant) / 2);
  const maxCandles = 30;
  const halfWin = Math.floor(maxCandles / 2);
  const rawStart = Math.max(0, tradeCenter - halfWin);
  const rawEnd = Math.min(chartData.length - 1, rawStart + maxCandles - 1);
  const startIdx = Math.max(0, rawEnd - maxCandles + 1);
  const endIdx = rawEnd;
  const slice = chartData.slice(startIdx, endIdx + 1);

  if (slice.length === 0) return null;

  const W = chartSize.w, H = chartSize.h, PAD_T = 30, PAD_B = 55, PAD_L = 60, PAD_R = 50;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  const highs = slice.map(c => c.high);
  const lows = slice.map(c => c.low);
  const priceMin = Math.min(...lows, trade.sl, trade.target) - 10;
  const priceMax = Math.max(...highs, trade.sl, trade.target) + 10;
  const priceRange = priceMax - priceMin || 1;

  const candleW = Math.min(18, Math.max(6, chartW / slice.length - 3));
  const gap = (chartW - candleW * slice.length) / (slice.length + 1);

  function xPos(i) { return PAD_L + gap * (i + 1) + candleW * i + candleW / 2; }
  function yPos(price) { return PAD_T + chartH - ((price - priceMin) / priceRange) * chartH; }
  function priceFromY(yCoord) { return priceMax - ((yCoord - PAD_T) / chartH) * priceRange; }

  const gridSteps = 6;
  const gridLines = [];
  for (let i = 0; i <= gridSteps; i++) gridLines.push(priceMin + (priceRange * i) / gridSteps);

  // Crosshair handler — uses refs to avoid re-renders
  function handleMouseMove(e) {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (W / rect.width);
    const my = (e.clientY - rect.top) * (H / rect.height);

    const visible = mx >= PAD_L && mx <= W - PAD_R && my >= PAD_T && my <= PAD_T + chartH;

    if (crossVRef.current) crossVRef.current.setAttribute("style", visible ? "" : "display:none");
    if (crossHRef.current) crossHRef.current.setAttribute("style", visible ? "" : "display:none");
    if (crossPriceRef.current) crossPriceRef.current.setAttribute("style", visible ? "" : "display:none");

    if (!visible) {
      if (ohlcBarRef.current) ohlcBarRef.current.textContent = "";
      return;
    }

    // Update crosshair lines directly
    if (crossVRef.current) { crossVRef.current.setAttribute("x1", mx); crossVRef.current.setAttribute("x2", mx); }
    if (crossHRef.current) { crossHRef.current.setAttribute("y1", my); crossHRef.current.setAttribute("y2", my); }
    if (crossPriceRef.current) crossPriceRef.current.setAttribute("y", my - 8);
    const price = priceFromY(my);
    if (crossPriceTextRef.current) { crossPriceTextRef.current.setAttribute("y", my + 4); crossPriceTextRef.current.textContent = price.toFixed(1); }

    // Find nearest candle for OHLC bar
    let nearestIdx = 0, nearestDist = Infinity;
    for (let i = 0; i < slice.length; i++) {
      const d = Math.abs(xPos(i) - mx);
      if (d < nearestDist) { nearestDist = d; nearestIdx = i; }
    }
    const c = slice[nearestIdx];
    if (ohlcBarRef.current && c) {
      const clr = c.close >= c.open ? "#69f0ae" : "#ff5252";
      ohlcBarRef.current.innerHTML =
        `<span>O: ${c.open?.toFixed(1)}</span>` +
        `<span class="high-val">H: ${c.high?.toFixed(1)}</span>` +
        `<span class="low-val">L: ${c.low?.toFixed(1)}</span>` +
        `<span style="color:${clr}">C: ${c.close?.toFixed(1)}</span>` +
        (c.ema5 ? `<span style="color:#ffc107">EMA5: ${c.ema5.toFixed(1)}</span>` : "") +
        (c.ema20 ? `<span style="color:#7c4dff">EMA20: ${c.ema20.toFixed(1)}</span>` : "") +
        `<span style="color:#546e7a">${formatDate(c.date)}</span>`;
    }
  }

  function handleMouseLeave() {
    if (crossVRef.current) crossVRef.current.setAttribute("style", "display:none");
    if (crossHRef.current) crossHRef.current.setAttribute("style", "display:none");
    if (crossPriceRef.current) crossPriceRef.current.setAttribute("style", "display:none");
    if (ohlcBarRef.current) ohlcBarRef.current.textContent = "";
  }

  return (
    <div className="chart-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="chart-modal chart-modal-large chart-resizable" ref={containerRef}>
        <div className="chart-modal-header">
          <span>
            <span className={`signal-badge ${trade.type === "SHORT" ? "badge-short" : "badge-long"}`}>{trade.type}</span>
            {" "}Entry: {trade.entry.toFixed(1)} | SL: {trade.sl.toFixed(1)} | TGT: {trade.target.toFixed(1)} | Exit: {trade.exitPrice.toFixed(1)} |{" "}
            <span className={trade.pnl >= 0 ? "positive" : "negative"}>
              {trade.pnl >= 0 ? "+" : ""}{trade.pnl.toFixed(1)} pts ({trade.exitReason})
            </span>
          </span>
          <button className="chart-close" onClick={onClose}>X</button>
        </div>

        {/* Crosshair OHLC bar — updated via ref, no re-renders */}
        <div className="chart-ohlc-bar" ref={ohlcBarRef}></div>

        <svg ref={svgRef} width="100%" height={H} viewBox={`0 0 ${W} ${H}`} className="candle-svg"
          onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} style={{cursor:"crosshair"}}>
          {/* Grid */}
          {gridLines.map((p, i) => (
            <g key={i}>
              <line x1={PAD_L} y1={yPos(p)} x2={W - PAD_R} y2={yPos(p)} stroke="var(--border-strong)" strokeWidth="1" />
              <text x={PAD_L - 6} y={yPos(p) + 4} fill="#78909c" fontSize="10" textAnchor="end">{Math.round(p)}</text>
            </g>
          ))}

          {/* Entry / SL / Target / Exit lines with collision-free labels */}
          {(() => {
            // Build label list, sort by Y position, then offset overlapping ones
            const labels = [
              { price: trade.entry, label: "Entry", color: "#42a5f5", dash: "6,3" },
              { price: trade.sl, label: "SL", color: "#ff5252", dash: "4,3" },
              { price: trade.target, label: "TGT", color: "#69f0ae", dash: "4,3" },
            ];
            if (exitIdx > 0) labels.push({ price: trade.exitPrice, label: "Exit", color: "#e040fb", dash: "3,3" });

            // Calculate Y positions and resolve collisions (min 14px apart)
            const items = labels.map(l => ({ ...l, rawY: yPos(l.price), labelY: yPos(l.price) + 4 }));
            items.sort((a, b) => a.rawY - b.rawY);
            for (let k = 1; k < items.length; k++) {
              if (items[k].labelY - items[k - 1].labelY < 14) {
                items[k].labelY = items[k - 1].labelY + 14;
              }
            }

            return items.map((item, k) => (
              <g key={k}>
                <line x1={PAD_L} y1={item.rawY} x2={W - PAD_R} y2={item.rawY}
                  stroke={item.color} strokeWidth="1" strokeDasharray={item.dash} />
                <rect x={W - PAD_R + 1} y={item.labelY - 10} width={48} height={13} rx="2" fill="var(--bg-deep)" opacity="0.85" />
                <text x={W - PAD_R + 3} y={item.labelY} fill={item.color} fontSize="9" fontWeight="600">
                  {item.label} {item.price.toFixed(0)}
                </text>
              </g>
            ));
          })()}

          {/* 20-EMA line */}
          <polyline fill="none" stroke="#7c4dff" strokeWidth="1" strokeDasharray="4,2"
            points={slice.map((c, i) => c.ema20 ? `${xPos(i)},${yPos(c.ema20)}` : "").filter(Boolean).join(" ")} />
          {/* 5-EMA line */}
          <polyline fill="none" stroke="#ffc107" strokeWidth="1.5"
            points={slice.map((c, i) => c.ema5 ? `${xPos(i)},${yPos(c.ema5)}` : "").filter(Boolean).join(" ")} />

          {/* Candlesticks */}
          {slice.map((c, i) => {
            const isBullish = c.close >= c.open;
            const bodyTop = yPos(Math.max(c.open, c.close));
            const bodyBot = yPos(Math.min(c.open, c.close));
            const bodyH = Math.max(bodyBot - bodyTop, 1);
            const globalIdx = startIdx + i;
            const isSignal = globalIdx === sigIdx;
            const isTrigger = globalIdx === trgIdx;
            const isExit = exitIdx > 0 && globalIdx === exitIdx;
            const fillC = isBullish ? "#69f0ae" : "#ff5252";
            const strokeC = isSignal ? "#42a5f5" : isTrigger ? "#ffc107" : isExit ? "#e040fb" : fillC;
            const strokeW = (isSignal || isTrigger || isExit) ? 2.5 : 1;

            return (
              <g key={i}>
                {isSignal && <rect x={xPos(i) - candleW / 2 - 3} y={PAD_T} width={candleW + 6} height={chartH} fill="rgba(66,165,245,0.08)" />}
                {isTrigger && <rect x={xPos(i) - candleW / 2 - 3} y={PAD_T} width={candleW + 6} height={chartH} fill="rgba(255,193,7,0.08)" />}
                {isExit && <rect x={xPos(i) - candleW / 2 - 3} y={PAD_T} width={candleW + 6} height={chartH} fill="rgba(224,64,251,0.08)" />}
                <line x1={xPos(i)} y1={yPos(c.high)} x2={xPos(i)} y2={yPos(c.low)} stroke={strokeC} strokeWidth={strokeW} />
                <rect x={xPos(i) - candleW / 2} y={bodyTop} width={candleW} height={bodyH}
                  fill={fillC} stroke={strokeC} strokeWidth={strokeW} opacity={0.9} />
                {isExit && <line x1={xPos(i) - candleW / 2 - 4} y1={yPos(trade.exitPrice)} x2={xPos(i) + candleW / 2 + 4} y2={yPos(trade.exitPrice)} stroke="#e040fb" strokeWidth="2.5" />}
                {isSignal && <text x={xPos(i)} y={H - 16} fill="#42a5f5" fontSize="9" fontWeight="700" textAnchor="middle">SIGNAL</text>}
                {isTrigger && <text x={xPos(i)} y={H - 16} fill="#ffc107" fontSize="9" fontWeight="700" textAnchor="middle">ENTRY</text>}
                {isExit && <text x={xPos(i)} y={H - 16} fill="#e040fb" fontSize="9" fontWeight="700" textAnchor="middle">EXIT</text>}
                {(i % Math.max(1, Math.floor(slice.length / 10)) === 0 || isSignal || isTrigger || isExit) && (
                  <text x={xPos(i)} y={H - 4} fill="#546e7a" fontSize="8" textAnchor="middle">
                    {formatDate(c.date).slice(0, 8)}
                  </text>
                )}
              </g>
            );
          })}

          {/* Crosshair — manipulated via refs, no re-renders */}
          <line ref={crossVRef} x1="0" y1={PAD_T} x2="0" y2={PAD_T + chartH} stroke="#ffffff33" strokeWidth="1" strokeDasharray="3,3" style={{display:"none"}} />
          <line ref={crossHRef} x1={PAD_L} y1="0" x2={W - PAD_R} y2="0" stroke="#ffffff33" strokeWidth="1" strokeDasharray="3,3" style={{display:"none"}} />
          <rect ref={crossPriceRef} x={W - PAD_R + 1} y="0" width={46} height={16} rx="3" fill="#37474f" style={{display:"none"}} />
          <text ref={crossPriceTextRef} x={W - PAD_R + 4} y="0" fill="var(--text)" fontSize="9"></text>

          {/* EMA labels */}
          <text x={PAD_L + 4} y={PAD_T - 8} fill="#ffc107" fontSize="10" fontWeight="600">5-EMA</text>
          <text x={PAD_L + 55} y={PAD_T - 8} fill="#7c4dff" fontSize="10" fontWeight="600">20-EMA</text>
        </svg>
      </div>
    </div>
  );
}

const TF_MINUTES = { "5minute": 5, "15minute": 15, "30minute": 30, "60minute": 60, "day": 375 };

function formatDateRange(d, tf) {
  if (!d) return "";
  const start = new Date(d);
  if (isNaN(start)) return d;
  const mins = TF_MINUTES[tf] || 15;
  const end = new Date(start.getTime() + mins * 60000);
  const fmt = (dt) => dt.toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
  const datePart = start.toLocaleString("en-IN", { day: "2-digit", month: "short" });
  return `${datePart}, ${fmt(start)}-${fmt(end)}`;
}

function formatDate(d) {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date)) return d;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function formatExpiry(d) {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date)) return d;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
}

function OptionTrade({ trade }) {
  if (!trade || !trade.suggestions || trade.suggestions.length === 0) return null;

  return (
    <div className="option-trade-box">
      <div className="option-trade-header">
        <span className={`option-direction ${trade.direction === "BEARISH" ? "dir-bearish" : "dir-bullish"}`}>
          {trade.direction === "BEARISH" ? "BUY PUT" : "BUY CALL"}
        </span>
        <span className="option-expiry">Exp: {formatExpiry(trade.expiry)}</span>
      </div>
      <div className="option-strikes">
        {trade.suggestions.map((s, i) => (
          <div key={i} className={`option-strike-row ${s.type === "ATM" ? "strike-atm" : "strike-otm"}`}>
            <div className="strike-info">
              <span className="strike-action">{s.action}</span>
              <span className="strike-value">{s.strike}</span>
              <span className={`strike-type-badge ${s.type === "ATM" ? "badge-atm" : "badge-otm"}`}>{s.type}</span>
            </div>
            <div className="strike-symbol">{s.symbol}</div>
            <div className="strike-reason">{s.reason}</div>
            <div className="strike-lot">Lot: {s.lotSize}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CandleRow({ candle, ema5, isSignal, isTrigger, signalType }) {
  const isBullish = candle.close >= candle.open;
  const aboveEma = candle.low > ema5;

  let rowClass = "candle-row";
  if (isSignal) rowClass += " candle-signal";
  if (isTrigger) rowClass += signalType === "SHORT" ? " candle-trigger-short" : " candle-trigger-long";

  return (
    <tr className={rowClass}>
      <td>{formatDate(candle.date)}</td>
      <td>{candle.open?.toFixed(1)}</td>
      <td className="high-val">{candle.high?.toFixed(1)}</td>
      <td className="low-val">{candle.low?.toFixed(1)}</td>
      <td className={isBullish ? "positive" : "negative"}>
        {candle.close?.toFixed(1)}
      </td>
      <td className="ema-val">{ema5?.toFixed(1) || "-"}</td>
      <td>
        {isSignal && (
          <span className={`signal-badge ${signalType === "SHORT" ? "badge-short" : "badge-long"}`}>
            SIGNAL
          </span>
        )}
        {isTrigger && (
          <span className={`signal-badge ${signalType === "SHORT" ? "badge-short" : "badge-long"}`}>
            {signalType === "SHORT" ? "SHORT" : "LONG"} TRIGGER
          </span>
        )}
        {!isSignal && !isTrigger && aboveEma && ema5 && (
          <span className="above-ema-dot" title="Above 5-EMA" />
        )}
      </td>
    </tr>
  );
}

/* ===== Stats Card Block ===== */
function StatsBlock({ label, stats, color }) {
  if (!stats || stats.trades === 0) return null;
  const pf = stats.profitFactor === Infinity ? "INF" : stats.profitFactor;
  return (
    <div className={`bt-breakdown-section bt-dir-section bt-dir-${color}`}>
      <h4>{label} <span className="bt-dir-count">({stats.trades} trades)</span></h4>
      <div className="bt-dir-summary">
        <div className={`bt-dir-pnl ${stats.totalOptionPnl >= 0 ? "positive" : "negative"}`}>
          {stats.totalOptionPnl >= 0 ? "+" : ""}₹{Math.abs(stats.totalOptionPnl || 0).toLocaleString("en-IN")}
        </div>
        <div className={`bt-dir-pnl ${stats.totalPnl >= 0 ? "positive" : "negative"}`} style={{fontSize:14}}>
          {stats.totalPnl >= 0 ? "+" : ""}{stats.totalPnl} pts
        </div>
        <div className={`bt-dir-wr ${stats.winRate >= 50 ? "positive" : "negative"}`}>
          {stats.winRate}% win
        </div>
        <div className={stats.profitFactor >= 1 ? "positive" : "negative"}>
          PF: {pf}
        </div>
      </div>
      <div className="bt-stat"><span>Wins / Losses</span><span>{stats.wins} / {stats.losses}</span></div>
      <div className="bt-stat"><span>Avg Win</span><span className="positive">+{stats.avgWin} pts</span></div>
      <div className="bt-stat"><span>Avg Loss</span><span className="negative">{stats.avgLoss} pts</span></div>
      <div className="bt-stat"><span>Best Trade</span><span className="positive">+{stats.maxWin} pts</span></div>
      <div className="bt-stat"><span>Worst Trade</span><span className="negative">{stats.maxLoss} pts</span></div>
      <div className="bt-stat"><span>Avg R</span><span className={stats.avgR >= 0 ? "positive" : "negative"}>{stats.avgR >= 0 ? "+" : ""}{stats.avgR}R</span></div>
      <div className="bt-stat"><span>Max Drawdown</span><span>{stats.maxDrawdown} pts</span></div>
      <div className="bt-stat"><span>Consec W / L</span><span>{stats.maxConsecWins} / {stats.maxConsecLosses}</span></div>
    </div>
  );
}

/* ===== Backtest Results Component ===== */
function BacktestResults({ bt, dirFilter }) {
  const s = bt.summary;
  const [chartTrade, setChartTrade] = useState(null);

  // Filter trades by direction
  const filteredTrades = dirFilter === "ALL"
    ? bt.trades
    : bt.trades.filter(t => t.type === dirFilter);

  // Build equity for filtered trades
  const filteredEquity = [];
  let cum = 0, cumOpt = 0;
  for (const t of filteredTrades) {
    cum += t.pnl;
    cumOpt += t.option?.moneyPnl || 0;
    filteredEquity.push({ date: t.exitDate, pnl: cum, optPnl: cumOpt, type: t.type });
  }

  // Compute quick stats for filtered view
  const fWins = filteredTrades.filter(t => t.result === "WIN").length;
  const fPnl = filteredTrades.reduce((s, t) => s + t.pnl, 0);
  const fWinRate = filteredTrades.length > 0 ? Math.round((fWins / filteredTrades.length) * 1000) / 10 : 0;

  return (
    <div className="backtest-results">
      <h3>Backtest Results — {bt.symbol} ({bt.timeframe}, {bt.period})</h3>
      <p className="bt-candle-count">
        {bt.totalCandles} candles analyzed
        {bt.dataSource && <span className="bt-data-source"> | {bt.dataSource}</span>}
      </p>

      {/* Actual premium stats */}
      {bt.premiumStats && (
        <div className="premium-stats-bar">
          <span className="premium-stats-icon">$</span>
          <span>
            <strong>{bt.premiumStats.actualDataTrades}</strong> of {bt.premiumStats.totalTrades} trades with actual option premiums
            {bt.premiumStats.skippedTrades > 0 && (
              <span className="premium-skipped"> | {bt.premiumStats.skippedTrades} skipped ({bt.premiumStats.reason})</span>
            )}
          </span>
        </div>
      )}

      {/* Option Money — Big highlight */}
      <div className={`bt-money-banner ${s.totalOptionPnl >= 0 ? "money-profit" : "money-loss"}`}>
        <div className="bt-money-amount">
          {s.totalOptionPnl >= 0 ? "+" : ""}₹{Math.abs(s.totalOptionPnl).toLocaleString("en-IN")}
        </div>
        <div className="bt-money-detail">
          {s.lots} lots × {s.lotSize} qty = {s.qty} units | {s.strikeType} {s.strikeType === "ATM" ? "(δ=0.50)" : "(δ=0.65)"} | {s.totalTrades} trades
          {s.totalCharges > 0 && <span className="charges-info"> | Charges: ₹{Math.round(s.totalCharges).toLocaleString("en-IN")} (Brokerage + STT + GST + Txn)</span>}
        </div>
        <div className="bt-enhancements">
          {s.useTrendFilter && <span className="enhance-badge">20-EMA Trend</span>}
          {s.useVolumeFilter && <span className="enhance-badge">Volume</span>}
          {s.useQualityFilter && <span className="enhance-badge">Quality</span>}
          {s.usePartialExit && <span className="enhance-badge">Partial Exit</span>}
          {s.avgHold > 0 && <span className="enhance-badge">Avg Hold: {s.avgHold} bars</span>}
          {s.partialExits > 0 && <span className="enhance-badge">Partial Exits: {s.partialExits}</span>}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="bt-summary-grid">
        <div className={`bt-card ${s.totalOptionPnl >= 0 ? "bt-card-green" : "bt-card-red"}`}>
          <div className="bt-card-value">{s.totalOptionPnl >= 0 ? "+" : ""}₹{Math.abs(s.totalOptionPnl).toLocaleString("en-IN")}</div>
          <div className="bt-card-label">Option P&L ({s.lots} lots)</div>
        </div>
        <div className={`bt-card ${s.totalPnl >= 0 ? "bt-card-green" : "bt-card-red"}`}>
          <div className="bt-card-value">{s.totalPnl >= 0 ? "+" : ""}{s.totalPnl} pts</div>
          <div className="bt-card-label">Index P&L</div>
        </div>
        <div className="bt-card">
          <div className="bt-card-value">{s.totalTrades}</div>
          <div className="bt-card-label">Total Trades</div>
        </div>
        <div className={`bt-card ${s.winRate >= 50 ? "bt-card-green" : "bt-card-red"}`}>
          <div className="bt-card-value">{s.winRate}%</div>
          <div className="bt-card-label">Win Rate</div>
        </div>
        <div className={`bt-card ${s.profitFactor >= 1 ? "bt-card-green" : "bt-card-red"}`}>
          <div className="bt-card-value">{s.profitFactor === Infinity ? "INF" : s.profitFactor}</div>
          <div className="bt-card-label">Profit Factor</div>
        </div>
        <div className="bt-card bt-card-red">
          <div className="bt-card-value">{s.maxDrawdown} pts</div>
          <div className="bt-card-label">Max Drawdown</div>
        </div>
        {s.capitalPerTrade > 0 && (
          <div className="bt-card">
            <div className="bt-card-value">₹{s.capitalPerTrade.toLocaleString("en-IN")}</div>
            <div className="bt-card-label">Capital/Trade</div>
          </div>
        )}
        {s.totalCapitalUsed > 0 && (
          <div className="bt-card">
            <div className="bt-card-value">₹{s.totalCapitalUsed.toLocaleString("en-IN")}</div>
            <div className="bt-card-label">Total Capital Used</div>
          </div>
        )}
        {s.roi !== undefined && s.roi !== 0 && (
          <div className={`bt-card ${s.roi >= 0 ? "bt-card-green" : "bt-card-red"}`}>
            <div className="bt-card-value">{s.roi >= 0 ? "+" : ""}{s.roi}%</div>
            <div className="bt-card-label">ROI</div>
          </div>
        )}
      </div>

      {/* SHORT vs LONG P&L Summary */}
      <div className="bt-dir-pnl-cards">
        <div className={`bt-dir-card bt-dir-card-short ${(bt.shortStats?.totalOptionPnl || 0) >= 0 ? "bt-card-green" : "bt-card-red"}`}>
          <div className="bt-dir-card-title">SHORT (Buy PUT)</div>
          <div className="bt-dir-card-optpnl">
            {(bt.shortStats?.totalOptionPnl || 0) >= 0 ? "+" : ""}₹{Math.abs(bt.shortStats?.totalOptionPnl || 0).toLocaleString("en-IN")}
          </div>
          <div className="bt-dir-card-meta">
            {bt.shortStats?.trades || 0} trades | {bt.shortStats?.winRate || 0}% win | {bt.shortStats?.totalPnl || 0} pts
          </div>
        </div>
        <div className={`bt-dir-card bt-dir-card-long ${(bt.longStats?.totalOptionPnl || 0) >= 0 ? "bt-card-green" : "bt-card-red"}`}>
          <div className="bt-dir-card-title">LONG (Buy CALL)</div>
          <div className="bt-dir-card-optpnl">
            {(bt.longStats?.totalOptionPnl || 0) >= 0 ? "+" : ""}₹{Math.abs(bt.longStats?.totalOptionPnl || 0).toLocaleString("en-IN")}
          </div>
          <div className="bt-dir-card-meta">
            {bt.longStats?.trades || 0} trades | {bt.longStats?.winRate || 0}% win | {bt.longStats?.totalPnl || 0} pts
          </div>
        </div>
      </div>

      {/* SHORT vs LONG Detailed Stats */}
      <div className="bt-breakdown">
        <StatsBlock label="SHORT (Buy PUT)" stats={bt.shortStats} color="short" />
        <StatsBlock label="LONG (Buy CALL)" stats={bt.longStats} color="long" />
      </div>

      {/* Overall Breakdown */}
      <div className="bt-breakdown">
        <div className="bt-breakdown-section">
          <h4>Overall Stats</h4>
          <div className="bt-stat"><span>Wins</span><span className="positive">{s.wins}</span></div>
          <div className="bt-stat"><span>Losses</span><span className="negative">{s.losses}</span></div>
          <div className="bt-stat"><span>Breakeven</span><span>{s.breakevens}</span></div>
          <div className="bt-stat"><span>Avg Win</span><span className="positive">+{s.avgWin} pts</span></div>
          <div className="bt-stat"><span>Avg Loss</span><span className="negative">{s.avgLoss} pts</span></div>
          <div className="bt-stat"><span>Best Trade</span><span className="positive">+{s.maxWin} pts</span></div>
          <div className="bt-stat"><span>Worst Trade</span><span className="negative">{s.maxLoss} pts</span></div>
          <div className="bt-stat"><span>Max Consec Wins</span><span>{s.maxConsecWins}</span></div>
          <div className="bt-stat"><span>Max Consec Losses</span><span>{s.maxConsecLosses}</span></div>
        </div>
        <div className="bt-breakdown-section">
          <h4>Settings</h4>
          <div className="bt-stat"><span>Target</span><span>{s.targetMultiple}R</span></div>
          <div className="bt-stat"><span>Trailing SL</span><span>{s.useTrailingSL ? "Yes" : "No"}</span></div>
          <div className="bt-stat"><span>Total R</span><span className={s.totalR >= 0 ? "positive" : "negative"}>{s.totalR >= 0 ? "+" : ""}{s.totalR}R</span></div>
        </div>
      </div>

      {/* Equity Curve — filtered */}
      {filteredEquity.length > 0 && (
        <div className="bt-equity">
          <h4>Equity Curve — {dirFilter === "ALL" ? "All Trades" : dirFilter + " Only"} ({filteredTrades.length} trades, {Math.round(fPnl * 10) / 10} pts)</h4>
          <div className="bt-equity-legend">
            <span className="legend-item"><span className="legend-line legend-pts"></span> Index (pts)</span>
            <span className="legend-item"><span className="legend-line legend-money"></span> Option (₹)</span>
          </div>
          <div className="bt-equity-chart">
            {(() => {
              const points = filteredEquity;
              const allVals = [...points.map(p => p.pnl), ...points.map(p => p.optPnl), 0];
              const maxVal = Math.max(...allVals);
              const minVal = Math.min(...allVals);
              const range = maxVal - minVal || 1;
              const h = 140;
              const w = points.length * 20 + 20;
              const yOf = (v) => h - ((v - minVal) / range) * h;
              return (
                <svg width="100%" height={h + 30} viewBox={`0 0 ${w} ${h + 30}`}>
                  {/* Zero line */}
                  <line x1="0" y1={yOf(0)} x2={w} y2={yOf(0)} stroke="var(--border-strong)" strokeDasharray="4" />
                  {/* Index pts line */}
                  <polyline
                    fill="none"
                    stroke={points[points.length - 1]?.pnl >= 0 ? "#69f0ae" : "#ff5252"}
                    strokeWidth="2"
                    points={points.map((p, i) => `${i * 20 + 10},${yOf(p.pnl)}`).join(" ")}
                  />
                  {/* Option ₹ line */}
                  <polyline
                    fill="none"
                    stroke="#ffc107"
                    strokeWidth="2"
                    strokeDasharray="6,3"
                    points={points.map((p, i) => `${i * 20 + 10},${yOf(p.optPnl)}`).join(" ")}
                  />
                  {/* Dots for index */}
                  {points.map((p, i) => (
                    <circle key={i} cx={i * 20 + 10} cy={yOf(p.pnl)} r="3"
                      fill={p.type === "SHORT" ? "#ff5252" : "#69f0ae"}>
                      <title>{`${p.type} ${formatDate(p.date)}: ${p.pnl >= 0 ? "+" : ""}${p.pnl.toFixed(1)} pts | ₹${p.optPnl >= 0 ? "+" : ""}${Math.round(p.optPnl).toLocaleString("en-IN")}`}</title>
                    </circle>
                  ))}
                </svg>
              );
            })()}
          </div>
        </div>
      )}

      {/* Trade Log — filtered */}
      <div className="bt-trade-log">
        <div className="trade-log-header">
          <h4>Trade Log — {dirFilter === "ALL" ? "All" : dirFilter} ({filteredTrades.length} trades)</h4>
          <div className="export-buttons">
            <button className="btn btn-export" onClick={() => exportTradesToCSV(filteredTrades, s, `5ema_${bt.timeframe}`)}>
              Export CSV
            </button>
            <button className="btn btn-sheets" onClick={() => openInGoogleSheets(filteredTrades, s)} title="Copies data to clipboard and opens Google Sheets — paste with Ctrl+V">
              Open in Sheets
            </button>
          </div>
        </div>
        <div className="stocks-table-wrapper">
          <table className="stocks-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Signal</th>
                <th>Exit Time</th>
                <th>Entry</th>
                <th>SL</th>
                <th>Target</th>
                <th>Exit</th>
                <th>Exit Reason</th>
                <th>P&L</th>
                <th>Option</th>
                <th>Buy Prem</th>
                <th>Sell Prem</th>
                <th>Capital</th>
                <th>₹ P&L (net)</th>
                <th>R</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrades.slice().reverse().map((t, idx) => (
                <React.Fragment key={idx}>
                <tr className={t.result === "WIN" ? "trade-win" : t.result === "LOSS" ? "trade-loss" : ""}>
                  <td>
                    <span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>
                      {t.type}
                    </span>
                  </td>
                  <td title={t.exactEntryTime ? new Date(t.exactEntryTime).toLocaleString("en-IN") : t.triggerDate}>
                    {t.exactEntryTime ? formatDate(t.exactEntryTime) : formatDateRange(t.triggerDate, bt.timeframe)}
                  </td>
                  <td title={t.exactExitTime ? new Date(t.exactExitTime).toLocaleString("en-IN") : t.exitDate}>
                    {t.exactExitTime ? formatDate(t.exactExitTime) : formatDateRange(t.exitDate, bt.timeframe)}
                  </td>
                  <td>
                    <a
                      href="#chart"
                      className="entry-link"
                      onClick={(e) => { e.preventDefault(); setChartTrade(t); }}
                      title="Click to view chart"
                    >
                      {t.entry.toFixed(1)}
                    </a>
                  </td>
                  <td className="sl">{t.sl.toFixed(1)}</td>
                  <td>{t.target.toFixed(1)}</td>
                  <td>{t.exitPrice.toFixed(1)}</td>
                  <td><span className={`exit-reason reason-${t.result.toLowerCase()}`}>{t.exitReason}</span></td>
                  <td className={t.pnl >= 0 ? "positive" : "negative"}>
                    {t.pnl >= 0 ? "+" : ""}{t.pnl.toFixed(1)}
                  </td>
                  <td className="opt-cell">
                    {t.option ? `${t.option.type} ${t.option.strike}` : "-"}
                    {t.option?.premiumSource === "ACTUAL" && <span className="actual-badge">ACTUAL</span>}
                    {t.option?.optionSymbol && <div className="opt-symbol-small">{t.option.optionSymbol}</div>}
                  </td>
                  <td className="premium-cell">
                    {t.option?.entryPremium ? `₹${t.option.entryPremium.toFixed(1)}` : "-"}
                  </td>
                  <td className="premium-cell">
                    {t.option?.exitPremium ? `₹${t.option.exitPremium.toFixed(1)}` : "-"}
                  </td>
                  <td className="premium-cell">
                    {t.option?.entryPremium ? `₹${Math.round(t.option.entryPremium * t.option.qty).toLocaleString("en-IN")}` : "-"}
                  </td>
                  <td className={t.option?.moneyPnl >= 0 ? "positive" : "negative"}
                    title={t.option ? `Gross: ₹${t.option.moneyPnlGross} | Charges: ₹${t.option.charges?.totalCharges}${t.option.thetaDecay ? ` | Theta: ₹${t.option.thetaDecay}` : ""}` : ""}>
                    {t.option ? `${t.option.moneyPnl >= 0 ? "+" : ""}₹${Math.abs(t.option.moneyPnl).toLocaleString("en-IN")}` : "-"}
                  </td>
                  <td className={t.rMultiple >= 0 ? "positive" : "negative"}>
                    {t.rMultiple >= 0 ? "+" : ""}{t.rMultiple.toFixed(2)}R
                  </td>
                </tr>
                {t.trailExitDone && (
                  <tr className="trail-detail-row">
                    <td colSpan={2} className="trail-detail-label">Exit 1: Trail SL</td>
                    <td>{Math.floor((t.option?.qty || 130) / 2)} qty ({Math.floor((t.option?.lots || 2) / 2) || 1}L)</td>
                    <td>{t.trailExitDate ? formatDate(t.trailExitDate) : "-"}</td>
                    <td colSpan={2}>{t.trailExitPrice?.toFixed(1)}</td>
                    <td className={t.trailExitPnl >= 0 ? "positive" : "negative"}>{t.trailExitPnl >= 0 ? "+" : ""}{t.trailExitPnl} pts</td>
                    <td colSpan={2} className="trail-detail-label">Exit 2: {t.exitReason}</td>
                    <td>{(t.option?.qty || 130) - Math.floor((t.option?.qty || 130) / 2)} qty</td>
                    <td>{t.exitPrice?.toFixed(1)}</td>
                    <td colSpan={3} className={t.pnl >= 0 ? "positive" : "negative"}>Combined: {t.pnl >= 0 ? "+" : ""}{t.pnl.toFixed(1)} pts</td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Candlestick Chart Modal */}
      {chartTrade && bt.chartData && (
        <CandleChart
          chartData={bt.chartData}
          trade={chartTrade}
          onClose={() => setChartTrade(null)}
        />
      )}
    </div>
  );
}

/* ===== Help Modal ===== */
function HelpModal({ onClose }) {
  return (
    <div className="confirm-overlay" onClick={onClose}>
      <div className="help-dialog" onClick={e => e.stopPropagation()}>
        <div className="help-header">
          <h3>5-EMA Strategy — Quick Guide</h3>
          <button className="chart-close" onClick={onClose}>X</button>
        </div>
        <div className="help-content">
          <div className="help-section">
            <h4>Strategy Rules</h4>
            <p><strong>SHORT (Buy PUT):</strong> Candle closes above 5-EMA with low above 5-EMA. Next candle breaks below signal candle's low → entry.</p>
            <p><strong>LONG (Buy CALL):</strong> Candle closes below 5-EMA with high below 5-EMA. Next candle breaks above signal candle's high → entry.</p>
            <p><strong>Stop Loss:</strong> Signal candle's high (for short) or low (for long).</p>
            <p><strong>Target:</strong> Risk × target multiple (e.g., 1:2 means target = 2× risk).</p>
          </div>

          <div className="help-section">
            <h4>Enhancement Filters</h4>
            <p><strong>20-EMA Trend:</strong> Skips counter-trend trades when 20-EMA is strongly trending against the signal direction.</p>
            <p><strong>Volume:</strong> Rejects signals where trigger candle volume is below 50% of 20-period average.</p>
            <p><strong>Signal Quality:</strong> Filters out tiny doji candles (&lt;10% ATR) and oversized candles (&gt;3× ATR). Also skips overextended setups.</p>
            <p><strong>Partial Exit:</strong> Books ~75% of lots when premium is +20% of capital, trails the remaining 25% to target.</p>
          </div>

          <div className="help-section">
            <h4>Scanner Controls</h4>
            <p><strong>Start Scan:</strong> Runs an initial scan immediately, then auto-repeats every 5 minutes. Click "Stop Scan" to pause.</p>
            <p><strong>Timeframe:</strong> Candle interval (5m, 15m, 30m, 1h, daily).</p>
            <p><strong>Days:</strong> Lookback period for historical candles.</p>
            <p><strong>Target:</strong> Risk-reward multiple (1:1 to 1:3).</p>
            <p><strong>Lots:</strong> Number of Nifty option lots (1 lot = 65 qty).</p>
            <p><strong>ATM/ITM:</strong> ATM = at-the-money (δ≈0.50), ITM = in-the-money (δ≈0.65).</p>
          </div>

          <div className="help-section">
            <h4>Auto-Trading</h4>
            <p><strong>Start Trading:</strong> Enables automatic order placement. When a confirmed signal appears during a scan, it places a MARKET BUY order for the recommended option.</p>
            <p><strong>Position Monitor:</strong> Fetches live Nifty LTP every 3 seconds when positions are open. Automatically exits when SL or target is hit. Stops polling when no open positions to save API calls.</p>
            <p><strong>Stop Trading:</strong> If open trades exist, you'll be asked to either square off all positions or keep them open for manual management.</p>
            <p><strong>Exit Button:</strong> Manually exit any individual open trade from the trade log.</p>
          </div>

          <div className="help-section">
            <h4>Backtest</h4>
            <p>Run historical backtests to validate the strategy. Configure target, trailing SL, lots, strike type, and enhancement filters independently from the scanner. Results show option P&L (₹), index P&L (pts), win rate, profit factor, equity curve, and per-trade log with charts.</p>
          </div>

          <div className="help-section help-warning">
            <h4>Important</h4>
            <p>Auto-trading places real orders with real money. Always verify your mStock account is authenticated and has sufficient margin before enabling. Use at your own risk.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function pickDeepItmStrike(optionChain, isPut) {
  const step = optionChain.strikeStep || 50;
  const spot = optionChain.spotPrice ?? optionChain.atmStrike;
  const base = Math.floor(spot / step) * step;
  return isPut ? base + 2 * step : base - step;
}

export default function FiveEma({ broker = "mstock" }) {
  const API = API_MAP[broker] || API_MAP.mstock;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timeframe, setTimeframe] = useState("15minute");
  const [days, setDays] = useState("1");

  // Scanner config (shared with scan)
  const [scanTarget, setScanTarget] = useState("3");
  const [scanLots, setScanLots] = useState("5");
  const [scanStrike, setScanStrike] = useState("ATM");

  // Auto-scan state
  const [scanning, setScanning] = useState(false);
  const [scanInterval, setScanInterval] = useState("10");
  const [lastScanTime, setLastScanTime] = useState(null);
  const scanIntervalRef = useRef(null);

  // Auto-trade state
  const [autoTrading, setAutoTrading] = useState(false);
  const [simulateMode, setSimulateMode] = useState(true); // default to simulate for safety
  const [tradeLog, setTradeLog] = useState([]);

  // Load persisted trades from backend on mount — only 5-ema trades
  useEffect(() => {
    axios.get("/api/monitor/status?strategy=5-ema").then(res => {
      if (res.data.success && res.data.trades?.length > 0) {
        setTradeLog(res.data.trades.map(t => ({
          ...t,
          id: t.id || Date.now() + Math.random(),
          time: t.placedAt || t.time,
          signalType: t.type === "SHORT" ? "PE" : "CE",
        })));
      }
    }).catch(() => {});
  }, []);
  const [tradeError, setTradeError] = useState("");
  const placingTradeRef = useRef(false);
  const processedSignalsRef = useRef(new Set());

  // Backtest state
  const [btData, setBtData] = useState(null);
  const [btLoading, setBtLoading] = useState(false);
  const [btError, setBtError] = useState("");
  const [btTimeframe, setBtTimeframe] = useState("15minute");
  const [btDays, setBtDays] = useState("1");
  const [btDateMode, setBtDateMode] = useState("custom"); // "days" or "custom" — default to calendar
  const [btFromDate, setBtFromDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [btToDate, setBtToDate] = useState(() => new Date().toISOString().split("T")[0]);
  // Multi-TF mode
  const [multiTf, setMultiTf] = useState(true); // default ON for backtest too
  const [shortTf, setShortTf] = useState("5minute");
  const [longTf, setLongTf] = useState("15minute");
  // Scanner multi-TF
  const [scanMultiTf, setScanMultiTf] = useState(true); // default ON: SHORT on 5min, LONG on 15min
  const [scanShortTf, setScanShortTf] = useState("5minute");
  const [scanLongTf, setScanLongTf] = useState("15minute");
  const [btTarget, setBtTarget] = useState("3");
  const [btTrailing, setBtTrailing] = useState(true);
  const [btDirFilter, setBtDirFilter] = useState("ALL");
  const [btLots, setBtLots] = useState("5");
  const [btStrike, setBtStrike] = useState("ATM");
  // Enhancement filters
  const [btTrend, setBtTrend] = useState(true);
  const [btVolume, setBtVolume] = useState(true);
  const [btQuality, setBtQuality] = useState(true);
  const [btPartial, setBtPartial] = useState(true);
  // Scanner filters
  const [scanTrend, setScanTrend] = useState(true);
  const [scanVolume, setScanVolume] = useState(true);
  const [scanQuality, setScanQuality] = useState(true);
  // Trade direction filter: BOTH | LONG | SHORT
  const [tradeDirection, setTradeDirection] = useState("BOTH");

  // --- Core scan function ---
  const runScan = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const commonParams = {
        days,
        trend: scanTrend ? "true" : "false",
        volume: scanVolume ? "true" : "false",
        quality: scanQuality ? "true" : "false",
      };

      if (scanMultiTf) {
        // Multi-TF: fetch both timeframes, keep SHORT from shortTf, LONG from longTf
        const [shortRes, longRes] = await Promise.all([
          axios.get(`${API}/five-ema`, { params: { ...commonParams, timeframe: scanShortTf }, timeout: 30000 }),
          axios.get(`${API}/five-ema`, { params: { ...commonParams, timeframe: scanLongTf }, timeout: 30000 }),
        ]);

        if (shortRes.data.success && longRes.data.success) {
          const shortData = shortRes.data.data;
          const longData = longRes.data.data;
          // Merge: SHORT signals from shortTf, LONG signals from longTf
          const shortSignals = (shortData.signals || []).filter(s => s.type === "SHORT").map(s => ({ ...s, sourceTimeframe: scanShortTf }));
          const longSignals = (longData.signals || []).filter(s => s.type === "LONG").map(s => ({ ...s, sourceTimeframe: scanLongTf }));
          const mergedSignals = [...shortSignals, ...longSignals].sort((a, b) => new Date(a.triggerCandle?.date) - new Date(b.triggerCandle?.date));

          // Use short TF data as base, override signals
          const merged = {
            ...shortData,
            signals: mergedSignals,
            totalSignals: mergedSignals.length,
            multiTf: true,
            shortTimeframe: scanShortTf,
            longTimeframe: scanLongTf,
            // Keep currentSetup from the relevant TF
            currentSetup: shortData.currentSetup?.type?.includes("BEARISH") ? shortData.currentSetup
              : longData.currentSetup?.type?.includes("BULLISH") ? longData.currentSetup
              : shortData.currentSetup || longData.currentSetup,
          };
          setData(merged);
          setLastScanTime(new Date());
          return merged;
        } else {
          setError(shortRes.data.message || longRes.data.message);
        }
      } else {
        // Single TF scan
        const res = await axios.get(`${API}/five-ema`, {
          params: { ...commonParams, timeframe },
          timeout: 30000,
        });
        if (res.data.success) {
          setData(res.data.data);
          setLastScanTime(new Date());
          return res.data.data;
        } else {
          setError(res.data.message);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
    return null;
  }, [timeframe, days, scanTrend, scanVolume, scanQuality, scanMultiTf, scanShortTf, scanLongTf]);

  // --- Auto-trade: place order when signal detected ---
  const placeAutoTrade = useCallback(async (signal, optionData) => {
    if (!signal || !optionData) return;

    const suggestion = optionData.suggestions?.[0];
    if (!suggestion) return;

    const lotSize = parseInt(suggestion.lotSize) || 65;
    const qty = parseInt(scanLots) * lotSize;

    try {
      let orderId = null;

      if (!simulateMode) {
        // LIVE: place real order
        const orderRes = await axios.post(`${API}/place-order`, {
          tradingsymbol: suggestion.symbol,
          exchange: "NFO",
          transaction_type: "BUY",
          quantity: qty,
          order_type: "MARKET",
          product: "MIS",
        });
        if (!orderRes.data.success) return null;
        orderId = orderRes.data.data?.orderId;
      } else {
        orderId = "SIM-" + Date.now();
      }

      // Fetch actual entry premium (both live and simulate)
      let entryPremium = null;
      try {
        const ltpRes = await axios.get(`${API}/option-ltp`, {
          params: { token: suggestion.token, tradingsymbol: suggestion.symbol },
          timeout: 5000,
        });
        if (ltpRes.data.success) entryPremium = ltpRes.data.ltp;
      } catch {}

      const capital = entryPremium ? Math.round(entryPremium * qty) : null;

      const tradeEntry = {
        id: Date.now(),
        time: new Date().toISOString(),
        type: signal.type,
        signalType: signal.type === "SHORT" ? "PE" : "CE",
        tradingsymbol: suggestion.symbol,
        optionToken: suggestion.token,
        strike: suggestion.strike,
        strikeType: suggestion.type,
        qty,
        lots: parseInt(scanLots),
        entry: signal.entryPrice,
        sl: signal.stopLoss,
        target: signal.type === "SHORT"
          ? signal.entryPrice - signal.risk * parseFloat(scanTarget)
          : signal.entryPrice + signal.risk * parseFloat(scanTarget),
        risk: signal.risk,
        orderId,
        status: "OPEN",
        simulated: simulateMode, strategy: "5-ema",
        entryPremium,
        exitPremium: null,
        capital,
        netPnl: null,
      };

      setTradeLog(prev => [tradeEntry, ...prev]);
      alertTradePlaced(tradeEntry);
      await axios.post(`${API}/auto-trade/track`, tradeEntry);
      return tradeEntry;
    } catch (err) {
      setTradeError(`${simulateMode ? "[SIM] " : ""}Order failed: ${err.response?.data?.message || err.message}`);
    }
    return null;
  }, [scanLots, scanTarget, simulateMode, API]);

  // --- Exit a trade manually ---
  const exitTrade = useCallback(async (trade, reason) => {
    try {
      // Fetch exit premium before placing exit order
      let exitPremium = null;
      if (trade.optionToken) {
        try {
          const ltpRes = await axios.get(`${API}/option-ltp`, { params: { token: trade.optionToken }, timeout: 5000 });
          if (ltpRes.data.success) exitPremium = ltpRes.data.ltp;
        } catch { /* use null */ }
      }

      let exitSuccess = false;
      let exitOrderId = null;

      if (!trade.simulated) {
        const res = await axios.post(`${API}/exit-order`, {
          tradingsymbol: trade.tradingsymbol,
          exchange: "NFO",
          quantity: trade.qty,
          transaction_type: "BUY",
        });
        exitSuccess = res.data.success;
        exitOrderId = res.data.data?.orderId;
      } else {
        exitSuccess = true;
        exitOrderId = "SIM-EXIT-" + Date.now();
      }

      if (exitSuccess) {
        // Calculate full charges and net P&L
        const ep = trade.entryPremium || 0;
        const xp = exitPremium || 0;
        const qty = trade.qty;
        const buyValue = ep * qty;
        const sellValue = xp * qty;
        const turnover = buyValue + sellValue;
        const brokerage = Math.min(20, turnover * 0.0003) * 2;
        const stt = sellValue * 0.000625;
        const txnCharges = turnover * 0.00053;
        const gst = (brokerage + txnCharges) * 0.18;
        const sebi = turnover * 0.000001;
        const stampDuty = buyValue * 0.00003;
        const totalCharges = Math.round((brokerage + stt + txnCharges + gst + sebi + stampDuty) * 100) / 100;
        const grossPnl = Math.round((xp - ep) * qty);
        const netPnl = Math.round(grossPnl - totalCharges);

        const closedTrade = {
                ...trade, status: "CLOSED",
                exitTime: new Date().toISOString(),
                exitOrderId,
                exitReason: reason || "MANUAL",
                exitPremium,
                grossPnl,
                charges: totalCharges,
                netPnl,
              };
        setTradeLog(prev => prev.map(t =>
          t.id === trade.id ? closedTrade : t
        ));
        alertTradeClosed(closedTrade);
        await axios.post(`${API}/auto-trade/update`, {
          id: trade.id, status: "CLOSED", exitTime: closedTrade.exitTime,
          exitReason: closedTrade.exitReason, exitPremium, grossPnl, netPnl, charges: totalCharges,
        }).catch(() => {});
      }
    } catch (err) {
      setTradeError(`Exit failed: ${err.response?.data?.message || err.message}`);
    }
  }, [API]);

  // --- Start/Stop auto-scan (5 min interval) ---
  const toggleScan = useCallback(() => {
    if (scanning) {
      // Stop scanning
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      setScanning(false);
    } else {
      // Start scanning — run immediately, then every 5 minutes
      setScanning(true);
      runScan();
      scanIntervalRef.current = setInterval(() => {
        runScan();
      }, parseInt(scanInterval) * 1000);
    }
  }, [scanning, runScan]);

  // Cleanup intervals on unmount
  const monitorRef = useRef(null);
  useEffect(() => {
    return () => {
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
      if (monitorRef.current) clearInterval(monitorRef.current);
    };
  }, []);

  // Backend monitor status polling
  const [liveNiftyPrice, setLiveNiftyPrice] = useState(null);
  const [monitorStatus, setMonitorStatus] = useState(null);

  useEffect(() => {
    if (!autoTrading && tradeLog.filter(t => t.status === "OPEN").length === 0) return;

    const pollStatus = async () => {
      try {
        const res = await axios.get("/api/monitor/status", { timeout: 5000 });
        if (res.data.success) {
          setMonitorStatus(res.data);
          // Get LTP for display
          const brokerKey = broker || "mstock";
          if (res.data.lastLTP?.[brokerKey]) setLiveNiftyPrice(res.data.lastLTP[brokerKey]);

          // Sync closed trades from backend to frontend
          for (const bt of (res.data.trades || []).filter(t => t.status === "CLOSED")) {
            setTradeLog(prev => prev.map(t => {
              if (t.tradingsymbol === bt.tradingsymbol && t.status === "OPEN") {
                const updated = { ...t, status: "CLOSED", exitTime: bt.exitTime, exitReason: bt.exitReason, exitPremium: bt.exitPremium };
                alertTradeClosed(updated);
                return updated;
              }
              return t;
            }));
          }
        }
      } catch {}
    };

    pollStatus();
    monitorRef.current = setInterval(pollStatus, 3000);
    return () => { if (monitorRef.current) { clearInterval(monitorRef.current); monitorRef.current = null; } };
  }, [autoTrading, tradeLog, broker]);

  // --- Auto-trade: check for signals (with lock to prevent duplicates) ---
  useEffect(() => {
    if (!autoTrading || !data?.signals?.length || placingTradeRef.current) return;

    const latestSignal = data.signals[data.signals.length - 1];
    if (tradeDirection !== "BOTH" && latestSignal.type !== tradeDirection) return;

    const signalKey = `${latestSignal.type}_${latestSignal.entryPrice}_${latestSignal.triggerCandle?.date}`;
    if (processedSignalsRef.current.has(signalKey)) return;

    const alreadyTraded = tradeLog.some(t => t.entry === latestSignal.entryPrice && t.type === latestSignal.type);
    if (alreadyTraded) return;

    let optionTrade = latestSignal.optionTrade;
    if (!optionTrade && data.optionChain?.strikes?.length > 0) {
      const atm = data.optionChain.atmStrike;
      const isPut = latestSignal.type === "SHORT";
      const want = pickDeepItmStrike(data.optionChain, isPut);
      const sd = data.optionChain.strikes.find(s => s.strike === want) || data.optionChain.strikes.find(s => s.strike === atm);
      const opt = isPut ? sd?.pe : sd?.ce;
      if (opt) optionTrade = { suggestions: [{ strike: sd.strike, symbol: opt.symbol, token: opt.token, lotSize: opt.lotSize || 65, type: "ITM+1" }] };
    }

    if (optionTrade) {
      placingTradeRef.current = true;
      processedSignalsRef.current.add(signalKey);
      placeAutoTrade(latestSignal, optionTrade).finally(() => { placingTradeRef.current = false; });
    }
  }, [data, autoTrading, tradeLog, placeAutoTrade, tradeDirection]);

  // --- Start/Stop auto-trading ---
  // State for stop-trading confirmation dialog
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const toggleAutoTrade = useCallback(async () => {
    if (autoTrading) {
      // Check for open trades before stopping
      const openTrades = tradeLog.filter(t => t.status === "OPEN");
      if (openTrades.length > 0) {
        setShowStopConfirm(true);
        return;
      }
      // No open trades — stop cleanly
      setAutoTrading(false);
      await axios.post(`${API}/auto-trade/stop`).catch(() => {});
    } else {
      setAutoTrading(true);
      await axios.post(`${API}/auto-trade/start`, {
        lots: parseInt(scanLots),
        strikeType: scanStrike,
        targetMultiple: parseFloat(scanTarget),
        useTrailingSL: true,
      }).catch(() => {});
      // Also start scanning if not already
      if (!scanning) toggleScan();
    }
  }, [autoTrading, tradeLog, scanLots, scanStrike, scanTarget, scanning, toggleScan]);

  // Handle stop-trading confirmation actions
  const handleStopSquareOff = useCallback(async () => {
    const openTrades = tradeLog.filter(t => t.status === "OPEN");
    for (const trade of openTrades) {
      await exitTrade(trade);
    }
    setAutoTrading(false);
    setShowStopConfirm(false);
    await axios.post(`${API}/auto-trade/stop`).catch(() => {});
  }, [tradeLog, exitTrade]);

  const handleStopKeepOpen = useCallback(async () => {
    setAutoTrading(false);
    setShowStopConfirm(false);
    await axios.post(`${API}/auto-trade/stop`).catch(() => {});
  }, []);

  async function runBacktest() {
    setBtLoading(true);
    setBtError("");
    try {
      const params = {
        timeframe: btTimeframe,
        days: btDateMode === "custom" ? "0" : btDays,
        ...(btDateMode === "custom" && { fromDate: btFromDate, toDate: btToDate }),
        target: btTarget,
        trailing: btTrailing ? "true" : "false",
        lots: btLots,
        strike: btStrike,
        trend: btTrend ? "true" : "false",
        volume: btVolume ? "true" : "false",
        quality: btQuality ? "true" : "false",
        partial: btPartial ? "true" : "false",
      };
      if (multiTf) {
        params.multiTf = "true";
        params.shortTf = shortTf;
        params.longTf = longTf;
      }
      const res = await axios.get(`${API}/backtest`, {
        params,
        timeout: 120000, // longer timeout for actual premium fetching from mStock
      });
      if (res.data.success) {
        setBtData(res.data.data);
      } else {
        setBtError(res.data.message);
      }
    } catch (err) {
      setBtError(err.response?.data?.message || err.message);
    }
    setBtLoading(false);
  }

  // Build a set of signal/trigger indices for highlighting
  const signalIndices = new Map();
  const triggerIndices = new Map();
  if (data?.signals) {
    data.signals.forEach((sig) => {
      signalIndices.set(sig.signalCandle.date, sig.type);
      triggerIndices.set(sig.triggerCandle.date, sig.type);
    });
  }

  return (
    <div className="five-ema">
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      <div className="dashboard-header">
        <div>
          <h2>5-EMA Strategy <button className="help-btn" onClick={() => setShowHelp(true)} title="Help & Instructions">?</button></h2>
          <p className="dashboard-subtitle">
            Nifty 50 (via Nifty Futures) — Scan for 5-EMA signals with option strike recommendations
          </p>
        </div>
        <div className="dashboard-actions">
          {!scanMultiTf ? (
            <select className="ema-select" value={timeframe} onChange={(e) => setTimeframe(e.target.value)} disabled={scanning}>
              <option value="5minute">5 Min</option>
              <option value="15minute">15 Min</option>
              <option value="30minute">30 Min</option>
              <option value="60minute">1 Hour</option>
              <option value="day">Daily</option>
            </select>
          ) : (
            <>
              <select className="ema-select tf-short" value={scanShortTf} onChange={e => setScanShortTf(e.target.value)} disabled={scanning} title="SHORT timeframe">
                <option value="5minute">Short: 5m</option>
                <option value="15minute">Short: 15m</option>
                <option value="30minute">Short: 30m</option>
                <option value="60minute">Short: 1h</option>
              </select>
              <select className="ema-select tf-long" value={scanLongTf} onChange={e => setScanLongTf(e.target.value)} disabled={scanning} title="LONG timeframe">
                <option value="5minute">Long: 5m</option>
                <option value="15minute">Long: 15m</option>
                <option value="30minute">Long: 30m</option>
                <option value="60minute">Long: 1h</option>
              </select>
            </>
          )}
          <select className="ema-select" value={days} onChange={(e) => setDays(e.target.value)} disabled={scanning}>
            <option value="1">1 Day</option>
            <option value="2">2 Days</option>
            <option value="3">3 Days</option>
            <option value="5">5 Days</option>
            <option value="10">10 Days</option>
            <option value="30">30 Days</option>
            <option value="60">60 Days</option>
            <option value="90">90 Days</option>
          </select>
          <select className="ema-select" value={scanTarget} onChange={(e) => setScanTarget(e.target.value)} disabled={scanning}>
            <option value="1">Target 1:1</option>
            <option value="1.5">Target 1:1.5</option>
            <option value="2">Target 1:2</option>
            <option value="3">Target 1:3</option>
            <option value="4">Target 1:4</option>
            <option value="5">Target 1:5</option>
          </select>
          <select className="ema-select" value={scanLots} onChange={(e) => setScanLots(e.target.value)} disabled={scanning}>
            <option value="1">1 Lot</option>
            <option value="2">2 Lots</option>
            <option value="3">3 Lots</option>
            <option value="5">5 Lots</option>
            <option value="10">10 Lots</option>
            <option value="15">15 Lots</option>
            <option value="20">20 Lots</option>
            <option value="25">25 Lots</option>
            <option value="50">50 Lots</option>
          </select>
          <select className="ema-select" value={scanStrike} onChange={(e) => setScanStrike(e.target.value)} disabled={scanning}>
            <option value="ATM">ATM</option>
            <option value="ITM">ITM</option>
          </select>
          <select className="ema-select" value={scanInterval} onChange={e => setScanInterval(e.target.value)} disabled={scanning}>
            <option value="10">10s</option>
            <option value="30">30s</option>
            <option value="60">1m</option>
            <option value="300">5m</option>
          </select>
          <button
            className={`btn ${scanning ? "btn-danger" : "btn-primary"}`}
            onClick={toggleScan}
            disabled={loading}
          >
            {loading ? "Scanning..." : scanning ? "Stop Scan" : "Start Scan"}
          </button>
          <select className="ema-select direction-select" value={tradeDirection} onChange={e => setTradeDirection(e.target.value)}>
            <option value="BOTH">Both</option>
            <option value="LONG">Long Only</option>
            <option value="SHORT">Short Only</option>
          </select>
          <button
            className={`btn ${autoTrading ? "btn-danger" : "btn-success"}`}
            onClick={toggleAutoTrade}
          >
            {autoTrading ? "Stop Trading" : "Start Trading"}
          </button>
          <label className={`sim-toggle ${simulateMode ? "sim-on" : "sim-off"}`} title={simulateMode ? "Paper trading — no real orders" : "LIVE — real orders will be placed!"}>
            <input type="checkbox" checked={simulateMode} onChange={e => setSimulateMode(e.target.checked)} disabled={autoTrading} />
            {simulateMode ? "SIMULATE" : "LIVE"}
          </label>
        </div>
      </div>

      {/* Enhancement Filters for Scanner */}
      <div className="ema-filters">
        <span className="filter-label">Filters:</span>
        <label className="filter-toggle">
          <input type="checkbox" checked={scanMultiTf} onChange={e => setScanMultiTf(e.target.checked)} />
          <span className="filter-name multi-tf-label">Multi-TF</span>
        </label>
        <label className="filter-toggle">
          <input type="checkbox" checked={scanTrend} onChange={(e) => setScanTrend(e.target.checked)} />
          <span className="filter-name">20-EMA Trend</span>
        </label>
        <label className="filter-toggle">
          <input type="checkbox" checked={scanVolume} onChange={(e) => setScanVolume(e.target.checked)} />
          <span className="filter-name">Volume</span>
        </label>
        <label className="filter-toggle">
          <input type="checkbox" checked={scanQuality} onChange={(e) => setScanQuality(e.target.checked)} />
          <span className="filter-name">Signal Quality</span>
        </label>
      </div>

      {/* Auto-Scan & Auto-Trade Status Bar */}
      {(scanning || autoTrading) && (
        <div className={`auto-status-bar ${autoTrading ? "auto-status-trading" : "auto-status-scanning"}`}>
          <div className="auto-status-left">
            {scanning && (
              <span className="auto-status-item">
                <span className="pulse-dot"></span>
                Auto-Scan ON ({parseInt(scanInterval) < 60 ? `${scanInterval}s` : `${Math.round(parseInt(scanInterval)/60)}m`})
                {lastScanTime && <span className="last-scan-time"> | Last: {lastScanTime.toLocaleTimeString("en-IN")}</span>}
              </span>
            )}
            {autoTrading && (
              <span className="auto-status-item auto-status-trade-on">
                <span className="pulse-dot pulse-green"></span>
                {simulateMode ? "PAPER TRADING" : "LIVE TRADING"} | {scanLots} lots | {scanStrike} | Target {scanTarget}R | {scanMultiTf ? `Short:${scanShortTf.replace("minute","m")} Long:${scanLongTf.replace("minute","m")}` : tradeDirection === "BOTH" ? "Long+Short" : tradeDirection + " Only"}
              </span>
            )}
          </div>
          <div className="auto-status-right">
            {liveNiftyPrice && tradeLog.some(t => t.status === "OPEN") && (
              <span className="live-nifty-price">
                Nifty: <strong>{liveNiftyPrice.toFixed(1)}</strong>
                <span className="monitor-speed"> {monitorStatus?.monitoring ? "(backend 3s)" : "(waiting)"}</span>
              </span>
            )}
            {tradeLog.filter(t => t.status === "OPEN").length > 0 && (
              <span className="open-trades-count">
                {tradeLog.filter(t => t.status === "OPEN").length} open trade(s)
              </span>
            )}
          </div>
        </div>
      )}

      {tradeError && <div className="error-msg">{tradeError}</div>}

      {/* Stop Trading Confirmation Dialog */}
      {showStopConfirm && (
        <div className="confirm-overlay" onClick={() => setShowStopConfirm(false)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">!</div>
            <h3>Active Trades Open</h3>
            <p>
              You have <strong>{tradeLog.filter(t => t.status === "OPEN").length}</strong> open trade(s).
              What would you like to do?
            </p>
            <div className="confirm-trades-list">
              {tradeLog.filter(t => t.status === "OPEN").map(t => (
                <div key={t.id} className="confirm-trade-item">
                  <span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>{t.type}</span>
                  <span>{t.signalType} {t.strike}</span>
                  <span>{t.qty} qty</span>
                </div>
              ))}
            </div>
            <div className="confirm-actions">
              <button className="btn btn-danger" onClick={handleStopSquareOff}>
                Square Off All & Stop
              </button>
              <button className="btn btn-warning" onClick={handleStopKeepOpen}>
                Keep Open & Stop
              </button>
              <button className="btn btn-secondary" onClick={() => setShowStopConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Trades Log */}
      {tradeLog.length > 0 && (
        <div className="trade-log-section">
          <div className="trade-log-header">
            <h3>Trade Log {autoTrading && <span className="live-badge">LIVE</span>}</h3>
            <div className="export-buttons">
              <button className="btn btn-export" onClick={() => exportLiveTradesCSV(tradeLog, "5ema-trades")}>Export CSV</button>
              <button className="btn btn-sheets" onClick={() => openLiveTradesInSheets(tradeLog)}>Open in Sheets</button>
            </div>
          </div>
          <div className="stocks-table-wrapper">
            <table className="stocks-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Option</th>
                  <th>Qty</th>
                  <th>Entry</th>
                  <th>SL</th>
                  <th>Target</th>
                  <th>Buy Prem</th>
                  <th>Sell Prem</th>
                  <th>Capital</th>
                  <th>Gross P&L</th>
                  <th>Charges</th>
                  <th>Net P&L</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {tradeLog.map((t) => (
                  <tr key={t.id} className={t.status === "OPEN" ? "trade-open-row" : "trade-closed-row"}>
                    <td>{new Date(t.time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}</td>
                    <td>
                      <span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>
                        {t.type}
                      </span>
                    </td>
                    <td className="opt-cell">{t.signalType} {t.strike}</td>
                    <td>{t.qty} ({t.lots}L){t.trailExitDone && (
                      <div className="trail-detail-label" style={{ fontSize: 10, marginTop: 2, fontWeight: 600 }}>
                        {(t.trailExitReason || "").includes("PROFIT") ? "Profit +20%" : "75% booked"}: {t.trailExitQty} @ ₹{t.trailExitPremium}
                        {t.trailExitPnl != null && <span className={t.trailExitPnl >= 0 ? "positive" : "negative"}> {t.trailExitPnl >= 0 ? "+" : ""}₹{t.trailExitPnl}</span>}
                        {t.remainingQty != null && <> · {t.remainingQty} trailing</>}
                      </div>
                    )}</td>
                    <td>{t.entry?.toFixed(1)}</td>
                    <td className="sl">{t.sl?.toFixed(1)}</td>
                    <td>{t.target?.toFixed(1)}</td>
                    <td className="premium-cell">{t.entryPremium ? `₹${t.entryPremium.toFixed(1)}` : "-"}</td>
                    <td className="premium-cell">{t.exitPremium ? `₹${t.exitPremium.toFixed(1)}` : "-"}</td>
                    <td className="premium-cell">{t.capital ? `₹${t.capital.toLocaleString("en-IN")}` : "-"}</td>
                    <td className={t.grossPnl != null ? (t.grossPnl >= 0 ? "positive" : "negative") : ""}>
                      {t.grossPnl != null ? `${t.grossPnl >= 0 ? "+" : ""}₹${Math.abs(t.grossPnl).toLocaleString("en-IN")}` : "-"}
                    </td>
                    <td className="premium-cell">{t.charges ? `₹${t.charges}` : "-"}</td>
                    <td className={t.netPnl != null ? (t.netPnl >= 0 ? "positive" : "negative") : ""}>
                      {t.netPnl != null ? `${t.netPnl >= 0 ? "+" : ""}₹${Math.abs(t.netPnl).toLocaleString("en-IN")}` : "-"}
                    </td>
                    <td>
                      <span className={`status-badge status-${t.status.toLowerCase()}`}>
                        {t.status}
                      </span>
                      {t.exitReason && <span className="exit-reason-text"> ({t.exitReason})</span>}
                    </td>
                    <td>
                      {t.status === "OPEN" && (
                        <button className="btn btn-sm btn-danger" onClick={() => exitTrade(t)}>
                          Exit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Strategy Rules */}
      <div className="ema-rules">
        <div className="ema-rule">
          <div className="ema-rule-icon short-icon">S</div>
          <div>
            <strong>Short Entry → Buy PUT (5 min)</strong>
            <p>
              Alert candle: low above 5-EMA → next candle breaks below alert candle's low → SHORT entry. SL: alert high. R:R 1:3
            </p>
          </div>
        </div>
        <div className="ema-rule">
          <div className="ema-rule-icon long-icon">L</div>
          <div>
            <strong>Long Entry → Buy CALL (15 min)</strong>
            <p>
              Alert candle: high below 5-EMA → next candle breaks above alert candle's high → LONG entry. SL: alert low. R:R 1:3
            </p>
          </div>
        </div>
        <div className="ema-rule">
          <div className="ema-rule-icon enhance-icon">+</div>
          <div>
            <strong>Enhancements</strong>
            <p>
              20-EMA trend filter | Volume confirmation | ATR-based quality filter | Partial exits at 1R | Wider trailing SL
            </p>
          </div>
        </div>
      </div>

      {/* ===== BACKTEST SECTION ===== */}
      <div className="bt-section">
        <div className="bt-header">
          <h3>Backtest</h3>
          <div className="bt-controls">
            <label className="filter-toggle" style={{marginRight:6}}>
              <input type="checkbox" checked={multiTf} onChange={e => setMultiTf(e.target.checked)} />
              <span className="filter-name multi-tf-label">Multi-TF</span>
            </label>
            {!multiTf ? (
              <select className="ema-select" value={btTimeframe} onChange={(e) => setBtTimeframe(e.target.value)}>
                <option value="5minute">5 Min</option>
                <option value="15minute">15 Min</option>
                <option value="30minute">30 Min</option>
                <option value="60minute">1 Hour</option>
                <option value="day">Daily</option>
              </select>
            ) : (
              <>
                <select className="ema-select tf-short" value={shortTf} onChange={e => setShortTf(e.target.value)}>
                  <option value="5minute">Short: 5m</option>
                  <option value="15minute">Short: 15m</option>
                  <option value="30minute">Short: 30m</option>
                  <option value="60minute">Short: 1h</option>
                </select>
                <select className="ema-select tf-long" value={longTf} onChange={e => setLongTf(e.target.value)}>
                  <option value="5minute">Long: 5m</option>
                  <option value="15minute">Long: 15m</option>
                  <option value="30minute">Long: 30m</option>
                  <option value="60minute">Long: 1h</option>
                </select>
              </>
            )}
            <select className="ema-select" value={btDateMode} onChange={e => setBtDateMode(e.target.value)}>
              <option value="days">By Days</option>
              <option value="custom">Custom Dates</option>
            </select>
            {btDateMode === "days" ? (
              <select className="ema-select" value={btDays} onChange={(e) => setBtDays(e.target.value)}>
                <option value="1">1 Day</option>
                <option value="2">2 Days</option>
                <option value="3">3 Days</option>
                <option value="5">5 Days</option>
                <option value="10">10 Days</option>
                <option value="30">30 Days</option>
                <option value="60">60 Days</option>
                <option value="90">90 Days</option>
              </select>
            ) : (
              <>
                <input type="date" className="ema-select date-input" value={btFromDate} onChange={e => setBtFromDate(e.target.value)} title="From date" />
                <input type="date" className="ema-select date-input" value={btToDate} onChange={e => setBtToDate(e.target.value)} title="To date" />
              </>
            )}
            <select className="ema-select" value={btTarget} onChange={(e) => setBtTarget(e.target.value)}>
              <option value="1">Target 1:1</option>
              <option value="1.5">Target 1:1.5</option>
              <option value="2">Target 1:2</option>
              <option value="3">Target 1:3</option>
              <option value="4">Target 1:4</option>
              <option value="5">Target 1:5</option>
            </select>
            <select className="ema-select" value={btLots} onChange={(e) => setBtLots(e.target.value)}>
              <option value="1">1 Lot</option>
              <option value="2">2 Lots</option>
              <option value="3">3 Lots</option>
              <option value="5">5 Lots</option>
              <option value="10">10 Lots</option>
              <option value="15">15 Lots</option>
              <option value="20">20 Lots</option>
              <option value="25">25 Lots</option>
              <option value="50">50 Lots</option>
            </select>
            <select className="ema-select" value={btStrike} onChange={(e) => setBtStrike(e.target.value)}>
              <option value="ATM">ATM (δ=0.50)</option>
              <option value="ITM">ITM (δ=0.65)</option>
            </select>
            <label className="bt-trailing-label">
              <input
                type="checkbox"
                checked={btTrailing}
                onChange={(e) => setBtTrailing(e.target.checked)}
              />
              Trailing SL
            </label>
            <label className="bt-trailing-label">
              <input
                type="checkbox"
                checked={btPartial}
                onChange={(e) => setBtPartial(e.target.checked)}
              />
              Partial Exit
            </label>
            <button className="btn btn-secondary" onClick={runBacktest} disabled={btLoading}>
              {btLoading ? "Fetching premiums..." : "Run Backtest"}
            </button>
          </div>
          <div className="bt-filters">
            <label className="filter-toggle">
              <input type="checkbox" checked={btTrend} onChange={(e) => setBtTrend(e.target.checked)} />
              <span className="filter-name">20-EMA Trend</span>
            </label>
            <label className="filter-toggle">
              <input type="checkbox" checked={btVolume} onChange={(e) => setBtVolume(e.target.checked)} />
              <span className="filter-name">Volume</span>
            </label>
            <label className="filter-toggle">
              <input type="checkbox" checked={btQuality} onChange={(e) => setBtQuality(e.target.checked)} />
              <span className="filter-name">Signal Quality</span>
            </label>
          </div>
        </div>

        {/* Direction Filter — shown after backtest runs */}
        {btData && !btLoading && (
          <div className="bt-dir-filter">
            <span className="bt-dir-label">Filter:</span>
            {["ALL", "SHORT", "LONG"].map((dir) => (
              <button
                key={dir}
                className={`bt-dir-btn ${btDirFilter === dir ? "active" : ""} ${dir === "SHORT" ? "btn-short" : dir === "LONG" ? "btn-long" : ""}`}
                onClick={() => setBtDirFilter(dir)}
              >
                {dir === "ALL" ? "All Trades" : dir === "SHORT" ? "SHORT Only" : "LONG Only"}
              </button>
            ))}
          </div>
        )}

        {btError && <div className="error-msg">{btError}</div>}

        {btLoading && (
          <div className="loading">
            <div className="spinner" />
            <p>Running backtest on historical data...</p>
          </div>
        )}

        {btData && !btLoading && <BacktestResults bt={btData} dirFilter={btDirFilter} />}
      </div>

      {error && <div className="error-msg">{error}</div>}

      {loading && (
        <div className="loading">
          <div className="spinner" />
          <p>Fetching Nifty data & option chain...</p>
        </div>
      )}

      {data && !loading && (
        <>
          {/* Live Watch Panel — what the scanner is looking for */}
          <div className="live-watch-panel">
            <div className="live-watch-header">
              <h4>Live Watch {scanning && <span className="pulse-dot" style={{display:"inline-block",marginLeft:8}}></span>}</h4>
              <span className="live-watch-time">Last scan: {lastScanTime ? lastScanTime.toLocaleTimeString("en-IN") : "-"}</span>
            </div>
            <div className="live-watch-grid">
              {/* Current Price & EMA Levels */}
              <div className="live-watch-card">
                <div className="live-watch-label">Nifty</div>
                <div className="live-watch-value">{data.lastCandle?.close?.toFixed(1)}</div>
              </div>
              <div className="live-watch-card">
                <div className="live-watch-label">5-EMA</div>
                <div className="live-watch-value" style={{color:"#ffc107"}}>{data.lastCandle?.ema5?.toFixed(1)}</div>
              </div>
              <div className="live-watch-card">
                <div className="live-watch-label">Position</div>
                <div className="live-watch-value" style={{color: data.lastCandle?.close > data.lastCandle?.ema5 ? "#69f0ae" : "#ff5252", fontSize: 13}}>
                  {data.lastCandle?.close > data.lastCandle?.ema5 ? "ABOVE 5-EMA" : "BELOW 5-EMA"}
                </div>
              </div>
              <div className="live-watch-card">
                <div className="live-watch-label">Looking For</div>
                <div className="live-watch-value" style={{fontSize: 12}}>
                  {data.lastCandle?.close > data.lastCandle?.ema5 && data.lastCandle?.low > data.lastCandle?.ema5
                    ? <span style={{color:"#ff5252"}}>SHORT — candle above EMA, waiting for next candle to break below <strong>{data.lastCandle?.low?.toFixed(1)}</strong></span>
                    : data.lastCandle?.close < data.lastCandle?.ema5 && data.lastCandle?.high < data.lastCandle?.ema5
                    ? <span style={{color:"#69f0ae"}}>LONG — candle below EMA, waiting for next candle to break above <strong>{data.lastCandle?.high?.toFixed(1)}</strong></span>
                    : <span style={{color:"#78909c"}}>No setup — candle straddles 5-EMA, waiting for separation</span>
                  }
                </div>
              </div>
            </div>
            {/* Probable Entry Levels */}
            {data.currentSetup && (
              <div className={`live-watch-setup ${data.currentSetup.type === "BEARISH_WATCH" ? "setup-bearish" : "setup-bullish"}`}>
                <div className="live-watch-setup-header">
                  <span className={data.currentSetup.type === "BEARISH_WATCH" ? "negative" : "positive"} style={{fontWeight:700}}>
                    {data.currentSetup.type === "BEARISH_WATCH" ? "PROBABLE SHORT ENTRY" : "PROBABLE LONG ENTRY"}
                  </span>
                </div>
                <div className="live-watch-levels">
                  {data.currentSetup.entryBelow && (
                    <div className="live-watch-level">
                      <span className="level-dot" style={{background:"#42a5f5"}}></span>
                      <span>Entry below: <strong>{data.currentSetup.entryBelow.toFixed(1)}</strong></span>
                    </div>
                  )}
                  {data.currentSetup.entryAbove && (
                    <div className="live-watch-level">
                      <span className="level-dot" style={{background:"#42a5f5"}}></span>
                      <span>Entry above: <strong>{data.currentSetup.entryAbove.toFixed(1)}</strong></span>
                    </div>
                  )}
                  <div className="live-watch-level">
                    <span className="level-dot" style={{background:"#ff5252"}}></span>
                    <span>Stop Loss: <strong>{data.currentSetup.stopLoss.toFixed(1)}</strong></span>
                  </div>
                  <div className="live-watch-level">
                    <span className="level-dot" style={{background:"#69f0ae"}}></span>
                    <span>Target ({scanTarget}R): <strong>
                      {data.currentSetup.type === "BEARISH_WATCH"
                        ? (data.currentSetup.entryBelow - (data.currentSetup.stopLoss - data.currentSetup.entryBelow) * parseFloat(scanTarget)).toFixed(1)
                        : (data.currentSetup.entryAbove + (data.currentSetup.entryAbove - data.currentSetup.stopLoss) * parseFloat(scanTarget)).toFixed(1)
                      }
                    </strong></span>
                  </div>
                  <div className="live-watch-level">
                    <span className="level-dot" style={{background:"#78909c"}}></span>
                    <span>Risk: <strong>{Math.abs((data.currentSetup.entryBelow || data.currentSetup.entryAbove) - data.currentSetup.stopLoss).toFixed(1)} pts</strong></span>
                  </div>
                </div>
                {data.currentSetup.optionTrade?.suggestions?.[0] && (
                  <div className="live-watch-option">
                    <span>{data.currentSetup.optionTrade.direction === "BEARISH" ? "BUY PUT" : "BUY CALL"}: </span>
                    <strong>{data.currentSetup.optionTrade.suggestions[0].symbol}</strong>
                    <span className="live-watch-opt-detail"> ({data.currentSetup.optionTrade.suggestions[0].type}, Strike {data.currentSetup.optionTrade.suggestions[0].strike})</span>
                  </div>
                )}
              </div>
            )}
            {!data.currentSetup && (
              <div className="live-watch-no-setup">No active setup — scanner is monitoring for signal candles</div>
            )}
          </div>

          {/* Current Setup Alert */}
          {data.currentSetup && (
            <div
              className={`ema-alert ${data.currentSetup.type === "BEARISH_WATCH" ? "alert-bearish" : "alert-bullish"}`}
            >
              <div className="ema-alert-header">
                <span className="ema-alert-type">
                  {data.currentSetup.type === "BEARISH_WATCH"
                    ? "ACTIVE BEARISH SETUP"
                    : "ACTIVE BULLISH SETUP"}
                </span>
                <span className="ema-alert-live">LIVE</span>
              </div>
              <p>{data.currentSetup.message}</p>
              <div className="ema-alert-levels">
                {data.currentSetup.entryBelow && (
                  <span>
                    Entry below: <strong>{data.currentSetup.entryBelow.toFixed(1)}</strong>
                  </span>
                )}
                {data.currentSetup.entryAbove && (
                  <span>
                    Entry above: <strong>{data.currentSetup.entryAbove.toFixed(1)}</strong>
                  </span>
                )}
                <span>
                  Stop Loss: <strong>{data.currentSetup.stopLoss.toFixed(1)}</strong>
                </span>
              </div>
              {data.currentSetup.optionTrade && (
                <OptionTrade trade={data.currentSetup.optionTrade} />
              )}
            </div>
          )}

          {/* Summary */}
          <div className="scan-summary">
            <div className="summary-card">
              <div className="summary-value">{data.symbol}</div>
              <div className="summary-label">Index</div>
            </div>
            <div className="summary-card">
              <div className="summary-value">
                {data.lastCandle?.close?.toFixed(1)}
              </div>
              <div className="summary-label">Spot Price</div>
            </div>
            <div className="summary-card">
              <div className="summary-value">
                {data.lastCandle?.ema5?.toFixed(1)}
              </div>
              <div className="summary-label">5-EMA</div>
            </div>
            <div className="summary-card">
              <div className="summary-value">{data.optionChain?.atmStrike}</div>
              <div className="summary-label">ATM Strike</div>
            </div>
            <div className="summary-card">
              <div className="summary-value">{data.totalSignals}</div>
              <div className="summary-label">Total Signals</div>
            </div>
          </div>

          {/* Option Chain Strip */}
          {data.optionChain && data.optionChain.strikes.length > 0 && (
            <div className="option-chain-section">
              <h3>Nifty Option Chain — {formatExpiry(data.optionChain.nearestExpiry)}</h3>
              <div className="stocks-table-wrapper">
                <table className="stocks-table option-chain-table">
                  <thead>
                    <tr>
                      <th>CE Symbol</th>
                      <th>CE Lot</th>
                      <th className="strike-col">Strike</th>
                      <th>PE Lot</th>
                      <th>PE Symbol</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.optionChain.strikes.map((s, idx) => (
                      <tr key={idx} className={s.isATM ? "atm-row" : ""}>
                        <td className="ce-cell">{s.ce?.symbol || "-"}</td>
                        <td>{s.ce?.lotSize || "-"}</td>
                        <td className={`strike-col ${s.isATM ? "atm-strike" : ""}`}>
                          {s.strike}
                          {s.isATM && <span className="atm-label">ATM</span>}
                        </td>
                        <td>{s.pe?.lotSize || "-"}</td>
                        <td className="pe-cell">{s.pe?.symbol || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent Signals with Strike Suggestions */}
          {data.signals.length > 0 && (
            <div className="ema-signals-list">
              <h3>Recent Signals with Option Trades</h3>
              {data.signals
                .slice()
                .reverse()
                .map((sig, idx) => (
                  <div
                    key={idx}
                    className={`ema-signal-card ${sig.type === "SHORT" ? "signal-short" : "signal-long"}`}
                  >
                    <div className="ema-signal-header">
                      <span
                        className={`signal-type-badge ${sig.type === "SHORT" ? "badge-short" : "badge-long"}`}
                      >
                        {sig.type}
                      </span>
                      {sig.strength !== undefined && (
                        <span className={`strength-badge ${sig.strength >= 70 ? "strength-strong" : sig.strength >= 50 ? "strength-moderate" : "strength-weak"}`}>
                          {sig.strength >= 70 ? "STRONG" : sig.strength >= 50 ? "MODERATE" : "WEAK"} ({sig.strength})
                        </span>
                      )}
                      <span className="signal-date">
                        {formatDate(sig.triggerCandle.date)}
                      </span>
                    </div>
                    <p className="signal-detail-text">{sig.detail}</p>
                    <div className="ema-signal-levels">
                      <div className="level">
                        <span className="level-label">Entry</span>
                        <span className="level-value">
                          {sig.entryPrice.toFixed(1)}
                        </span>
                      </div>
                      <div className="level">
                        <span className="level-label">Stop Loss</span>
                        <span className="level-value sl">
                          {sig.stopLoss.toFixed(1)}
                        </span>
                      </div>
                      <div className="level">
                        <span className="level-label">Risk</span>
                        <span className="level-value">
                          {sig.risk.toFixed(1)} pts
                        </span>
                      </div>
                      <div className="level">
                        <span className="level-label">5-EMA</span>
                        <span className="level-value ema-val">
                          {sig.emaAtSignal.toFixed(1)}
                        </span>
                      </div>
                    </div>
                    {sig.optionTrade && <OptionTrade trade={sig.optionTrade} />}
                  </div>
                ))}
            </div>
          )}

          {/* Candle Chart Table */}
          <div className="ema-chart-table">
            <h3>Candle Data with 5-EMA</h3>
            <div className="stocks-table-wrapper">
              <table className="stocks-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Open</th>
                    <th>High</th>
                    <th>Low</th>
                    <th>Close</th>
                    <th>5-EMA</th>
                    <th>Signal</th>
                  </tr>
                </thead>
                <tbody>
                  {data.chartData
                    .slice()
                    .reverse()
                    .map((candle, idx) => (
                      <CandleRow
                        key={idx}
                        candle={candle}
                        ema5={candle.ema5}
                        isSignal={signalIndices.has(candle.date)}
                        isTrigger={triggerIndices.has(candle.date)}
                        signalType={
                          signalIndices.get(candle.date) ||
                          triggerIndices.get(candle.date)
                        }
                      />
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!data && !loading && !error && !btData && (
        <div className="scan-empty">
          <h3>5-EMA Nifty Scanner</h3>
          <p>
            Select timeframe and period, then click "Scan Nifty" to find 5-EMA
            signals with Nifty 50 option strike recommendations. Use "Run Backtest"
            to verify strategy performance on historical data.
          </p>
        </div>
      )}
    </div>
  );
}
